import { skipToken, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { queueApi } from '../../data/api/queue.api'
import { storyboardsApi } from '../../data/api/storyboards.api'
import type { GenerateStoryboardParams } from '../../data/types/GenerationParams'
import { showToast } from '../../utils/fluentToast'
import { ActivityAction } from '../enums/ActivityAction'
import { ActivityStatus } from '../enums/ActivityStatus'
import {
  getImageStyleBatchFailure,
  type ActiveImageStyleJob,
  type ApplyImageStyleParams,
  type ImageStyleJob,
} from '../models/ImageStyleBatch'
import type { QueueActivityDetails } from '../models/QueueActivity'
import type { Storyboard } from '../models/Storyboard'

import { commercialsKeys } from './useCommercials'
import { refreshImageProvidersOnInvalidProviderError } from './useImageProviders'
import { queueKeys } from './useQueue'
import { shotsKeys } from './useShots'

export const storyboardsKeys = {
  all: ['storyboards'] as const,
  detail: (id: string | undefined) => ['storyboards', 'detail', id] as const,
  generate: (id: string) => ['storyboards', 'generate', id] as const,
  export: (id: string | undefined) => ['storyboards', 'export', id] as const,
  applyStyle: (id: string | undefined) => ['storyboards', 'apply-style', id] as const,
  styleStatus: (id: string | undefined, activityId: string) =>
    ['storyboards', 'style-status', id, activityId] as const,
}

export function useStoryboardApplyStyle(commercialId: string | undefined) {
  const qc = useQueryClient()
  const trackAccepted = async (jobs: ImageStyleJob[]) => {
    if (!jobs.length) return
    await qc.cancelQueries({ queryKey: storyboardsKeys.detail(commercialId) })
    qc.setQueryData<Storyboard>(storyboardsKeys.detail(commercialId), (current) => {
      if (!current) return current
      const activeStyleJobs = new Map(current.activeStyleJobs?.map((job) => [job.activityId, job]))
      for (const job of jobs) {
        const source = current.entries.find((entry) => entry.imageId === job.imageId)
        if (!source?.variantId) continue
        activeStyleJobs.set(job.activityId, {
          activityId: job.activityId,
          shotId: source.shotId,
          variantId: source.variantId,
          sourceImageId: job.imageId,
          imageStylePromptId: job.imageStylePromptId,
          status: ActivityStatus.Pending,
        })
      }
      return { ...current, activeStyleJobs: [...activeStyleJobs.values()] }
    })
  }
  return useMutation({
    mutationKey: storyboardsKeys.applyStyle(commercialId),
    retry: false,
    mutationFn: (params: ApplyImageStyleParams) => {
      if (!commercialId) throw new Error('A commercial is required')
      return storyboardsApi.applyStyle(commercialId, params).then((r) => r.data)
    },
    onSuccess: (result) => trackAccepted(result.jobs),
    onSettled: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: queueKeys.all }),
        qc.invalidateQueries({ queryKey: storyboardsKeys.detail(commercialId) }),
      ])
    },
    onError: async (error) => {
      refreshImageProvidersOnInvalidProviderError(error, qc)
      const partial = getImageStyleBatchFailure(error)
      if (partial) {
        await trackAccepted(partial.jobs)
      }
    },
  })
}
export function useStoryboard(commercialId: string | undefined) {
  const qc = useQueryClient()
  const query = useQuery({
    queryKey: storyboardsKeys.detail(commercialId),
    queryFn: commercialId
      ? async () => {
          const { data } = await storyboardsApi.detail(commercialId)
          const cached = qc.getQueryData<Storyboard>(storyboardsKeys.detail(commercialId))
          const active = new Map(
            [...(cached?.activeStyleJobs ?? []), ...(data.activeStyleJobs ?? [])].map((job) => [
              job.activityId,
              job,
            ]),
          )
          return { ...data, activeStyleJobs: [...active.values()] }
        }
      : skipToken,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  })
  const progress = useStyleProgress(commercialId, query.data)
  return {
    ...query,
    data: query.data ? { ...query.data, activeStyleJobs: progress.jobs } : undefined,
    isStyleStatusError: progress.isError || (query.isError && Boolean(query.data)),
    refetchStyleStatus: async () => {
      await Promise.all([query.refetch(), ...progress.statuses.map((status) => status.refetch())])
    },
  }
}

function isStylingActive(status: ActivityStatus | undefined) {
  return status === ActivityStatus.Pending || status === ActivityStatus.Running
}

function useStyleProgress(commercialId: string | undefined, storyboard: Storyboard | undefined) {
  const qc = useQueryClient()
  const completed = useRef(new Set<string>())
  const [tracking, setTracking] = useState<{
    commercialId: string | undefined
    jobs: Record<string, ActiveImageStyleJob>
  }>({ commercialId, jobs: {} })
  const tracked = tracking.commercialId === commercialId ? tracking.jobs : {}

  useEffect(() => {
    setTracking((current) => {
      const jobs = current.commercialId === commercialId ? { ...current.jobs } : {}
      let changed = current.commercialId !== commercialId
      for (const job of storyboard?.activeStyleJobs ?? []) {
        if (
          !Object.prototype.hasOwnProperty.call(jobs, job.activityId) &&
          !completed.current.has(`${commercialId}:${job.activityId}`)
        ) {
          jobs[job.activityId] = job
          changed = true
        }
      }
      return changed ? { commercialId, jobs } : current
    })
  }, [commercialId, storyboard?.activeStyleJobs])

  const jobs = Object.values(tracked)
  const statuses = useQueries({
    queries: jobs.map((job) => ({
      queryKey: storyboardsKeys.styleStatus(commercialId, job.activityId),
      queryFn: async () => {
        const { data } = await queueApi.detail(job.activityId)
        if (!data.status || data.status === ActivityStatus.None) {
          throw new Error('The image styling activity status is unavailable.')
        }
        return data
      },
      enabled: Boolean(commercialId),
      refetchOnMount: 'always' as const,
      refetchOnWindowFocus: 'always' as const,
      refetchOnReconnect: 'always' as const,
      refetchInterval: (query: { state: { data: QueueActivityDetails | undefined } }) =>
        !query.state.data || isStylingActive(query.state.data.status) ? 5000 : false,
    })),
  })

  useEffect(() => {
    jobs.forEach((job, index) => {
      const activity = statuses[index]?.data
      const key = `${commercialId}:${job.activityId}`
      if (!activity?.status || isStylingActive(activity.status) || completed.current.has(key))
        return
      completed.current.add(key)
      qc.setQueryData<Storyboard>(storyboardsKeys.detail(commercialId), (current) =>
        current
          ? {
              ...current,
              activeStyleJobs: current.activeStyleJobs?.filter(
                (active) => active.activityId !== job.activityId,
              ),
            }
          : current,
      )
      setTracking((current) => {
        if (current.commercialId !== commercialId) return current
        const next = Object.fromEntries(
          Object.entries(current.jobs).filter(([activityId]) => activityId !== job.activityId),
        )
        return { ...current, jobs: next }
      })
      void qc.invalidateQueries({ queryKey: shotsKeys.variant(job.shotId, job.variantId) })
      void qc.invalidateQueries({ queryKey: shotsKeys.variant(job.shotId), exact: true })
      void qc.invalidateQueries({ queryKey: storyboardsKeys.detail(commercialId) })
      void qc.invalidateQueries({ queryKey: queueKeys.all })
      if (activity.status !== ActivityStatus.Completed) {
        showToast(
          `Image style ${activity.status.toLowerCase()} for image ${job.sourceImageId}, style ${job.imageStylePromptId} (activity ${job.activityId}). Check activities for details.`,
          'error',
        )
      }
    })
  }, [commercialId, jobs, qc, statuses])

  return {
    jobs: jobs.filter((job, index) => {
      const status = statuses[index]?.data?.status
      return !status || isStylingActive(status)
    }),
    isError: statuses.some((status) => status.isError),
    statuses,
  }
}

export function useStoryboardGenerate(commercialId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: GenerateStoryboardParams) =>
      storyboardsApi.generate(commercialId, params).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queueKeys.all })
      qc.invalidateQueries({
        queryKey: commercialsKeys.status(commercialId, ActivityAction.Storyboard),
      })
      qc.invalidateQueries({ queryKey: shotsKeys.all })
    },
  })
}

export function useStoryboardExport(commercialId: string | undefined) {
  return useMutation({
    mutationFn: commercialId ? () => storyboardsApi.export(commercialId) : undefined,
  })
}
